import { create } from 'zustand';
import { getBackendApiClient } from '@/features/backend-api';
import type { BackendApiError } from '@shared/types';

const ACCESS_TOKEN_KEY = 'auth.accessToken';
const REFRESH_TOKEN_KEY = 'auth.refreshToken';

type AuthStatus = 'signed-out' | 'signed-in';
type AuthMode = 'login' | 'register' | 'forgot-password' | 'forgot-password-sent';

interface AuthState {
  status: AuthStatus;
  isModalOpen: boolean;
  mode: AuthMode;
  isSubmitting: boolean;
  error: string | null;

  openModal: (mode: AuthMode) => void;
  closeModal: () => void;
  setMode: (mode: AuthMode) => void;

  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  logout: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
}

function readInitialStatus(): AuthStatus {
  return localStorage.getItem(ACCESS_TOKEN_KEY) ? 'signed-in' : 'signed-out';
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: readInitialStatus(),
  isModalOpen: false,
  mode: 'login',
  isSubmitting: false,
  error: null,

  openModal: (mode) => set({ isModalOpen: true, mode, error: null }),
  closeModal: () => set({ isModalOpen: false, error: null }),
  setMode: (mode) => set({ mode, error: null }),

  login: async (email, password) => {
    set({ isSubmitting: true, error: null });
    try {
      const tokens = await getBackendApiClient().login({ email, password });
      localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
      set({ status: 'signed-in', isModalOpen: false, isSubmitting: false });
    } catch (err) {
      set({ isSubmitting: false, error: mapAuthError(err) });
    }
  },

  register: async (email, password, displayName) => {
    set({ isSubmitting: true, error: null });
    try {
      await getBackendApiClient().register({ email, password, displayName });
      await get().login(email, password);
    } catch (err) {
      set({ isSubmitting: false, error: mapAuthError(err) });
    }
  },

  logout: async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    set({ status: 'signed-out' });
    if (refreshToken) {
      try {
        await getBackendApiClient().logout(refreshToken);
      } catch {
        // токены уже локально сброшены — молча игнорируем ошибку revoke
      }
    }
  },

  // email
  requestPasswordReset: async () => {
  set({ isSubmitting: true, error: null });
  try {
    // TODO: заменить на реальный вызов, когда бэкенд добавит эндпоинт:
    // await getBackendApiClient().forgotPassword({ email });
    await new Promise((resolve) => setTimeout(resolve, 500)); // временная заглушка
    set({ isSubmitting: false, mode: 'forgot-password-sent' });
  } catch (err) {
    set({ isSubmitting: false, error: mapAuthError(err) });
  }
},
}));

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

function mapAuthError(err: unknown): string {
  const apiErr = err as BackendApiError;
  if (apiErr?.message) return apiErr.message;
  return 'Что-то пошло не так. Попробуйте ещё раз.';
}